import { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { ROLES, useAuthState } from 'src/contexts/authContext';
import {
  advanceActionLabel,
  canEditGroupTrial,
  GroupTrial,
  nextGroupTrialStatus,
  quotaSummary,
  statusLabel
} from 'src/models/groupTrial';
import { ADMIN_RETRIEVE_GROUP_TRIALS } from './operations';
import GroupTrialFormModal from './GroupTrialFormModal';
import AdvanceGroupTrialStatusDialog from './AdvanceGroupTrialStatusDialog';

type Props = {
  // The organization whose group trials to manage. Undefined when the user has
  // no organization yet.
  organizationId?: string;
};

const GroupTrialsSection = ({ organizationId }: Props) => {
  const authState = useAuthState();

  const canManageGroupTrials = authState.hasAuthRole([
    ROLES.ADMIN,
    ROLES.DEV_ADMIN
  ]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTrial, setEditTrial] = useState<GroupTrial | undefined>();
  const [advanceTrial, setAdvanceTrial] = useState<GroupTrial | undefined>();

  const { data, loading, error, refetch } = useQuery(
    ADMIN_RETRIEVE_GROUP_TRIALS,
    {
      variables: { organizationId },
      skip: !canManageGroupTrials || !organizationId,
      fetchPolicy: 'cache-and-network'
    }
  );

  // The trial-group controls are strictly ADMIN / DEV_ADMIN — stricter than the
  // enclosing Organization tab. Non-privileged users see nothing at all.
  if (!canManageGroupTrials) {
    return null;
  }

  if (!organizationId) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Trial Groups
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Add an organization first to create and manage group trials.
        </Typography>
      </Box>
    );
  }

  const trials: GroupTrial[] = data?.AdminRetrieveGroupTrials ?? [];

  const handleSaved = () => {
    refetch();
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Typography variant="h5">Trial Groups</Typography>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Create trial group
        </Button>
      </Box>

      {loading && !data && <CircularProgress size={24} />}
      {error && <FormHelperText error>{error.message}</FormHelperText>}

      {!loading && trials.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          This organization has no group trials yet.
        </Typography>
      )}

      {trials.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Max participants</TableCell>
              <TableCell>Quotas</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trials.map((trial) => {
              const editable = canEditGroupTrial(trial.status);
              const advanceable = !!nextGroupTrialStatus(trial.status);
              return (
                <TableRow key={trial.id}>
                  <TableCell>{trial.name}</TableCell>
                  <TableCell>{statusLabel(trial.status)}</TableCell>
                  <TableCell align="right">{trial.maxParticipants}</TableCell>
                  <TableCell>{quotaSummary(trial.assessmentQuotas)}</TableCell>
                  <TableCell>{trial.created?.formatted ?? '—'}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      disabled={!editable}
                      onClick={() => setEditTrial(trial)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      disabled={!advanceable}
                      onClick={() => setAdvanceTrial(trial)}
                    >
                      {advanceActionLabel(trial.status)}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {createOpen && (
        <GroupTrialFormModal
          open={createOpen}
          organizationId={organizationId}
          onClose={() => setCreateOpen(false)}
          onSaved={handleSaved}
        />
      )}

      {editTrial && (
        <GroupTrialFormModal
          open={!!editTrial}
          organizationId={organizationId}
          existingTrial={editTrial}
          onClose={() => setEditTrial(undefined)}
          onSaved={handleSaved}
        />
      )}

      {advanceTrial && (
        <AdvanceGroupTrialStatusDialog
          open={!!advanceTrial}
          organizationId={organizationId}
          trial={advanceTrial}
          onClose={() => setAdvanceTrial(undefined)}
          onAdvanced={handleSaved}
        />
      )}
    </Box>
  );
};

export default GroupTrialsSection;
